import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Briefcase, Layers, Users, ShieldCheck,
  FileText, Wallet, CalendarDays, BookOpen, BarChart3, ListChecks,
  FolderOpen, FileBarChart, UserCog, Settings, LineChart, UploadCloud, History, Plug,
  Coins, Lock, Activity, GitBranch, Building, Palette, HelpCircle, TrendingUp, Layers as LayersIcon, Percent, Repeat, Calculator, Inbox, Mail, Sparkles, ScrollText, LifeBuoy, FilePenLine, ChevronDown, Search, X, Trash2, Map, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import { canSeeRoute } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";

const DEFAULT_BRAND = {
  name: "AIFLedger",
  tagline: "",
  primary_color: "#0b192c",
  accent_color: "#c4a454",
  logo_url: "/corpzo-icon-sm.png",
  is_root: true,
};

const PLATFORM_DEFAULT_LOGO = "/corpzo-icon-sm.png";

const NAV = [
  { section: "Overview", items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" }] },
  {
    section: "Fund Setup",
    items: [
      { to: "/clients", label: "AIF Clients", icon: Building2, testId: "nav-clients" },
      { to: "/funds", label: "Fund Master", icon: Briefcase, testId: "nav-funds" },
      { to: "/schemes", label: "Scheme Master", icon: Layers, testId: "nav-schemes" },
      { to: "/fund-classes", label: "Fund Classes", icon: LayersIcon, testId: "nav-fund-classes" },
    ],
  },
  {
    section: "Investor",
    items: [
      { to: "/investors", label: "Investor CRM", icon: Users, testId: "nav-investors" },
      { to: "/kyc-review", label: "KYC Review", icon: ShieldCheck, testId: "nav-kyc" },
    ],
  },
  {
    section: "Capital",
    items: [
      { to: "/commitments", label: "Commitment Register", icon: FileText, testId: "nav-commitments" },
      { to: "/capital-calls", label: "Capital Call Register", icon: Wallet, testId: "nav-capital-calls" },
      { to: "/payments", label: "Payment Reconciliation", icon: ListChecks, testId: "nav-payments" },
      { to: "/bank-recon", label: "Bank Statement Recon", icon: UploadCloud, testId: "nav-bank-recon" },
      { to: "/distributions", label: "Distributions", icon: Coins, testId: "nav-distributions" },
      { to: "/open-ended", label: "Open-Ended Ops", icon: Repeat, testId: "nav-open-ended" },
      { to: "/hwm", label: "HWM & Side-Pockets", icon: Calculator, testId: "nav-hwm" },
    ],
  },
  {
    section: "Accounting",
    items: [
      { to: "/ledger", label: "Fund Ledger", icon: BookOpen, testId: "nav-ledger" },
      { to: "/chart-of-accounts", label: "Chart of Accounts", icon: ListChecks, testId: "nav-chart-of-accounts" },
      { to: "/journal", label: "Journal Entries", icon: FileText, testId: "nav-journal" },
      { to: "/nav", label: "NAV & Valuation", icon: LineChart, testId: "nav-nav" },
      { to: "/derivatives", label: "Derivatives MTM", icon: Activity, testId: "nav-derivatives" },
      { to: "/waterfall", label: "Waterfall", icon: GitBranch, testId: "nav-waterfall" },
    ],
  },
  {
    section: "Portfolio",
    items: [
      { to: "/portfolio", label: "Portfolio Holdings", icon: BarChart3, testId: "nav-portfolio" },
      { to: "/investees", label: "Investees & Deals", icon: Building, testId: "nav-investees" },
      { to: "/deal-workflow", label: "Deal Workflow", icon: GitBranch, testId: "nav-deal-workflow" },
      { to: "/ops", label: "Advanced Ops", icon: Activity, testId: "nav-ops" },
      { to: "/analytics", label: "Analytics & Reports", icon: TrendingUp, testId: "nav-analytics" },
    ],
  },
  {
    section: "Ops & Compliance",
    items: [
      { to: "/compliance", label: "Compliance Calendar", icon: CalendarDays, testId: "nav-compliance" },
      { to: "/taxation", label: "Taxation", icon: Percent, testId: "nav-taxation" },
      { to: "/tasks", label: "Task Manager", icon: ListChecks, testId: "nav-tasks" },
      { to: "/documents", label: "Document Vault", icon: FolderOpen, testId: "nav-documents" },
      { to: "/reports", label: "Report Centre", icon: FileBarChart, testId: "nav-reports" },
      { to: "/report-templates", label: "Template Designer", icon: ScrollText, testId: "nav-report-templates" },
      { to: "/bulk-upload", label: "Bulk Upload", icon: UploadCloud, testId: "nav-bulk-upload" },
      { to: "/ingestion", label: "BENPOS & Custody", icon: UploadCloud, testId: "nav-ingestion" },
      { to: "/audit", label: "Audit Trail", icon: History, testId: "nav-audit" },
      { to: "/compliance/history", label: "Sign History", icon: ShieldCheck, testId: "nav-sign-history" },
    ],
  },
  {
    section: "Admin",
    items: [
      { to: "/users", label: "Users & Roles", icon: UserCog, testId: "nav-users" },
      { to: "/distributors", label: "Distributors", icon: Users, testId: "nav-distributors" },
      { to: "/admin/billing", label: "Billing & Plans", icon: Wallet, testId: "nav-billing-admin" },
      { to: "/admin/pricing", label: "Pricing & Overrides", icon: Coins, testId: "nav-pricing" },
      { to: "/admin/digest", label: "Digest Recipients", icon: Mail, testId: "nav-admin-digest" },
      { to: "/admin/lp-nudge", label: "LP Nudge Preview", icon: Inbox, testId: "nav-lp-nudge" },
    ],
  },
  {
    section: "Settings",
    items: [
      { to: "/settings", label: "Admin Settings", icon: Settings, testId: "nav-settings" },
      { to: "/settings/branding", label: "Practice Identity", icon: Palette, testId: "nav-tenant-branding" },
      { to: "/branding", label: "PDF Branding", icon: Palette, testId: "nav-branding" },
      { to: "/security", label: "Security & MFA", icon: Lock, testId: "nav-security" },
      { to: "/integrations", label: "Integrations", icon: Plug, testId: "nav-integrations" },
      { to: "/settings/leegality", label: "Leegality e-Sign", icon: FilePenLine, testId: "nav-leegality" },
      { to: "/help", label: "User Manual", icon: HelpCircle, testId: "nav-help" },
    ],
  },
  {
    section: "Leads",
    items: [
      { to: "/leads", label: "Leads Inbox", icon: Inbox, testId: "nav-leads" },
      { to: "/admin/demos", label: "Demo Accounts", icon: Sparkles, testId: "nav-demos" },
      { to: "/proposal-templates", label: "Proposal Templates", icon: FilePenLine, testId: "nav-proposal-templates" },
    ],
  },
  {
    section: "Platform",
    items: [
      { to: "/platform/practices", label: "Practices", icon: Building, testId: "nav-platform-practices" },
      { to: "/platform/impersonate", label: "Impersonate", icon: UserCog, testId: "nav-platform-impersonate" },
      { to: "/owner/brand", label: "Platform Brand", icon: Palette, testId: "nav-owner-brand" },
      { to: "/owner/audit-log", label: "Owner Audit", icon: ScrollText, testId: "nav-owner-audit" },
      { to: "/owner/trash", label: "Owner Trash", icon: Trash2, testId: "nav-owner-trash" },
      { to: "/owner/bulk-cleanup", label: "Bulk Cleanup", icon: Trash2, testId: "nav-owner-bulk-cleanup" },
      { to: "/owner/landing", label: "Landing CMS", icon: Sparkles, testId: "nav-owner-landing" },
      { to: "/owner/whats-new", label: "Announcements", icon: Mail, testId: "nav-owner-whats-new" },
      { to: "/owner/support", label: "Support Inbox", icon: LifeBuoy, testId: "nav-owner-support" },
      { to: "/owner/sitemap", label: "Sitemap · All URLs", icon: Map, testId: "nav-owner-sitemap" },
    ],
  },
];

const INVESTOR_NAV = [
  {
    section: "My Portfolio",
    items: [
      { to: "/investor", label: "Dashboard", icon: LayoutDashboard, testId: "nav-investor-dashboard" },
      { to: "/investor/kyc", label: "KYC", icon: ShieldCheck, testId: "nav-investor-kyc" },
      { to: "/investor/statements", label: "Statements", icon: FileText, testId: "nav-investor-statements" },
      { to: "/investor/documents", label: "Documents", icon: FolderOpen, testId: "nav-investor-documents" },
      { to: "/investor/redeem", label: "Redeem Units", icon: Repeat, testId: "nav-investor-redeem" },
    ],
  },
  {
    section: "Account",
    items: [
      { to: "/security", label: "Security & MFA", icon: Lock, testId: "nav-investor-security" },
      { to: "/help", label: "User Manual", icon: HelpCircle, testId: "nav-investor-help" },
    ],
  },
];

const DISTRIBUTOR_NAV = [
  {
    section: "My Book",
    items: [
      { to: "/distributor", label: "Portfolio", icon: LayoutDashboard, testId: "nav-distributor-portfolio" },
    ],
  },
  {
    section: "Account",
    items: [
      { to: "/profile", label: "Profile", icon: UserCog, testId: "nav-distributor-profile" },
      { to: "/billing", label: "Subscription", icon: Wallet, testId: "nav-distributor-billing" },
      { to: "/security", label: "Security & MFA", icon: Lock, testId: "nav-distributor-security" },
    ],
  },
];

export default function Sidebar({ activeRole, mobileOpen = false, onClose, isDemo = false, isPlatformAdmin = false, roles = [] }) {
  const { user } = useAuth() || {};
  const brand = { ...DEFAULT_BRAND, ...(user?.practice_branding || {}) };
  // Global platform logo (Owner-configurable). Shown on the left of the
  // sidebar for every tenant. When the current practice is a non-root
  // tenant with its own logo, we render THAT logo as a small co-brand chip
  // on the right — "AIFLedger × Motilal Wealth" style.
  const platformLogo = user?.platform_brand?.logo_url || PLATFORM_DEFAULT_LOGO;
  const isRootPractice = brand.is_root;
  const showCoBrand = !isRootPractice && brand.logo_url && brand.logo_url !== PLATFORM_DEFAULT_LOGO;
  const raw = activeRole === "investor" ? INVESTOR_NAV
            : activeRole === "distributor" ? DISTRIBUTOR_NAV
            : NAV;
  const nav = raw
    .map((sec) => ({ ...sec, items: sec.items.filter((it) => canSeeRoute(activeRole, it.to, { isPlatformAdmin, roles })) }))
    // Demo prospects never see the Admin group — even though their role is
    // fund_admin, we hide anything that would expose cross-tenant surface.
    .filter((sec) => !(isDemo && sec.section === "Admin"))
    // The Leads and Platform sections are exclusive to platform_admin accounts.
    .filter((sec) => !["Leads", "Platform"].includes(sec.section) || isPlatformAdmin)
    .filter((sec) => sec.items.length > 0);
  return (
    <aside
      className={`aif-sidebar w-64 flex-shrink-0 h-screen overflow-y-auto flex flex-col
        fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-200
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      data-testid="sidebar"
    >
      <div className="px-5 py-6 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0" data-testid="sidebar-brand">
          <img src={platformLogo} alt={brand.name || "AIFLedger"}
               className="h-9 w-9 flex-shrink-0 rounded object-contain bg-white/5 p-0.5"
               data-testid="sidebar-brand-logo" />
          <div className="min-w-0 flex-1">
            {isRootPractice && user?.platform_brand?.wordmark_url ? (
              <div className="bg-white/95 rounded px-2 py-1 inline-block max-w-full"
                   data-testid="sidebar-brand-name">
                <img src={user.platform_brand.wordmark_url}
                     alt="AIFLedger"
                     className="h-6 object-contain"
                     data-testid="sidebar-brand-wordmark" />
              </div>
            ) : (
              <div className="font-serif text-2xl text-white tracking-tight leading-none truncate"
                   data-testid="sidebar-brand-name">
                {brand.name}<span style={{ color: brand.accent_color || "#c4a454" }}>.</span>
              </div>
            )}
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/50 mt-1.5 font-semibold truncate"
                 data-testid="sidebar-brand-tagline">
              {brand.is_root
                ? (isDemo ? "by Corpzo · Demo · Sandbox"
                    : activeRole === "investor" ? "by Corpzo · LP Portal"
                    : activeRole === "distributor" ? "by Corpzo · Distributor"
                    : "Institutional Fund Administration")
                : `${brand.tagline || "Fund Administration"}`}
            </div>
          </div>
          {showCoBrand && (
            <div className="ml-1 flex items-center gap-1.5 pl-2 border-l border-white/10 flex-shrink-0"
                 data-testid="sidebar-cobrand"
                 title={`Co-branded with ${brand.name}`}>
              <span className="text-[10px] text-white/40 font-serif">×</span>
              <img src={brand.logo_url} alt={`${brand.name} co-brand`}
                   className="h-7 w-7 rounded object-contain bg-white/10 p-0.5"
                   data-testid="sidebar-cobrand-logo" />
            </div>
          )}
        </div>
        {/* Close button on mobile */}
        <button
          className="lg:hidden text-white/60 hover:text-white"
          onClick={onClose}
          data-testid="sidebar-close"
          aria-label="Close menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      {/* Jump-to search — filters flat across every module, or focus with `/` */}
      <SidebarJumpTo nav={nav} onNavigate={onClose} />

      <nav className="flex-1 py-3 px-2 space-y-1" data-testid="sidebar-nav">
        <CollapsibleNav nav={nav} />
      </nav>
      <div className="px-5 py-4 border-t border-white/5 text-[10px] text-white/40 uppercase tracking-wider">
        Build 1.0 · SEBI-aligned
      </div>
    </aside>
  );
}


// ── Collapsible nav ─────────────────────────────────────────────────────────
// 17 modules is too many for a flat list. Each section header is a button
// that toggles its group open/closed; the state persists to localStorage so
// the operator&apos;s layout survives refreshes. `Overview` always renders
// its single Dashboard link inline (no toggle) — collapsing it would just
// hide the most important link on the app. The section containing the
// currently-active route is force-opened on every render so navigating a
// link never lands you on a page whose sidebar entry is invisible.
const COLLAPSE_STORAGE_KEY = "aif_sidebar_collapsed_v1";

// Default: everything except Overview + the active section starts collapsed
// so a new user sees a calm, short sidebar instead of the full 17-module wall.
// Overview and the section currently in view expand automatically.
function loadCollapsedSet() {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return null;
    return new Set(JSON.parse(raw));
  } catch { return null; }
}
function saveCollapsedSet(set) {
  try { window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(set))); }
  catch { /* ignore */ }
}

function CollapsibleNav({ nav }) {
  const location = useLocation();
  // Which section holds the currently-active route? That section is force
  // expanded (even if the user previously collapsed it), so navigating never
  // hides the link that produced the current page.
  const activeSection = useMemo(() => {
    for (const sec of nav) {
      if (sec.items.some((it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"))) {
        return sec.section;
      }
    }
    return null;
  }, [nav, location.pathname]);

  // First-run default: collapse every group except Overview + the active one.
  const [collapsed, setCollapsed] = useState(() => {
    const saved = loadCollapsedSet();
    if (saved) return saved;
    // No saved preference → collapse everything except Overview + whatever
    // section owns the initial route.
    const s = new Set();
    for (const sec of nav) {
      if (sec.section !== "Overview") s.add(sec.section);
    }
    return s;
  });

  useEffect(() => { saveCollapsedSet(collapsed); }, [collapsed]);

  const toggle = (section) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Sections eligible for the bulk toggles (everything except Overview,
  // which is always flat, and the section owning the current route which is
  // force-expanded — collapsing it wouldn't do anything visible anyway).
  const collapsibleSections = useMemo(
    () => nav.filter((s) => s.section !== "Overview").map((s) => s.section),
    [nav],
  );
  const collapsedEligibleCount = collapsibleSections.filter((s) => collapsed.has(s)).length;
  const allCollapsed = collapsedEligibleCount === collapsibleSections.length;
  const allExpanded = collapsedEligibleCount === 0;

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collapsibleSections));

  return (
    <>
      {/* Expand-all / Collapse-all toolbar */}
      {collapsibleSections.length > 1 ? (
        <div className="flex items-center justify-between px-3 pb-2 pt-1"
             data-testid="nav-bulk-toolbar">
          <span className="text-[9px] uppercase tracking-[0.18em] text-white/30 font-semibold">
            {collapsibleSections.length - collapsedEligibleCount}/{collapsibleSections.length} open
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAll}
              disabled={allExpanded}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors
                ${allExpanded ? "text-white/20 cursor-not-allowed" : "text-white/60 hover:text-white hover:bg-white/5"}`}
              title="Expand all sections"
              aria-label="Expand all sections"
              data-testid="nav-expand-all"
            >
              <ChevronsUpDown size={11} strokeWidth={2.2} /> Expand
            </button>
            <button
              type="button"
              onClick={collapseAll}
              disabled={allCollapsed}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors
                ${allCollapsed ? "text-white/20 cursor-not-allowed" : "text-white/60 hover:text-white hover:bg-white/5"}`}
              title="Collapse all sections"
              aria-label="Collapse all sections"
              data-testid="nav-collapse-all"
            >
              <ChevronsDownUp size={11} strokeWidth={2.2} /> Collapse
            </button>
          </div>
        </div>
      ) : null}

      {nav.map((sec) => {
        // Overview renders flat (no toggle) — its single Dashboard link is
        // the primary landing surface and must never be hidden.
        if (sec.section === "Overview") {
          return (
            <div key={sec.section} className="mb-1" data-testid={`nav-section-${sec.section}`}>
              <div className="nav-section" data-testid={`nav-section-label-${sec.section}`}>{sec.section}</div>
              {sec.items.map((it) => <NavItem key={it.to} it={it} />)}
            </div>
          );
        }

        const isForceOpen = sec.section === activeSection;
        const isOpen = isForceOpen || !collapsed.has(sec.section);
        const activeCount = sec.items.filter((it) =>
          location.pathname === it.to || location.pathname.startsWith(it.to + "/")
        ).length;

        return (
          <div key={sec.section} className="mb-1" data-testid={`nav-section-${sec.section}`}>
            <button
              type="button"
              onClick={() => !isForceOpen && toggle(sec.section)}
              className={`nav-section w-full flex items-center justify-between hover:text-white transition-colors ${isForceOpen ? "cursor-default" : "cursor-pointer"}`}
              aria-expanded={isOpen}
              data-testid={`nav-section-toggle-${sec.section}`}
            >
              <span className="flex items-center gap-2">
                {sec.section}
                {activeCount > 0 && !isOpen ? (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#c4a454" }} />
                ) : null}
              </span>
              <ChevronDown
                size={11}
                strokeWidth={2.5}
                className={`transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"} text-white/40`}
                data-testid={`nav-section-chevron-${sec.section}`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${isOpen ? "opacity-100" : "max-h-0 opacity-0"}`}
              style={isOpen ? {} : { maxHeight: 0 }}
              data-testid={`nav-section-body-${sec.section}`}
            >
              {sec.items.map((it) => <NavItem key={it.to} it={it} />)}
            </div>
          </div>
        );
      })}
    </>
  );
}

function NavItem({ it }) {
  return (
    <NavLink to={it.to} end
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      data-testid={it.testId}
    >
      <it.icon size={15} strokeWidth={1.6} />
      <span>{it.label}</span>
    </NavLink>
  );
}


// ── Jump-to search ──────────────────────────────────────────────────────────
// A flat, always-visible search over every nav item. Case-insensitive substring
// match against `label` OR `section`. Keyboard: `/` focuses the input from
// anywhere except a text field; `Esc` clears + blurs; `↑ ↓` walk results;
// `Enter` opens the highlighted match. Auto-closes the mobile drawer after
// a hit so the operator lands directly on the target page.
function SidebarJumpTo({ nav, onNavigate }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Flatten the section→items tree for fast substring filtering. `section` is
  // carried on each row so we can render a subtle context label under matches.
  const flat = useMemo(() => {
    const out = [];
    for (const sec of nav) {
      for (const it of sec.items) {
        out.push({ ...it, section: sec.section });
      }
    }
    return out;
  }, [nav]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return flat
      .filter((it) =>
        (it.label || "").toLowerCase().includes(term) ||
        (it.section || "").toLowerCase().includes(term)
      )
      .slice(0, 12);
  }, [flat, q]);

  useEffect(() => { setCursor(0); }, [q]);

  // Global "/" hotkey — but not when the user is already typing somewhere.
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const editable = tag === "INPUT" || tag === "TEXTAREA" || (e.target && e.target.isContentEditable);
      if (!editable && e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const openHit = (hit) => {
    setQ("");
    navigate(hit.to);
    onNavigate && onNavigate();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && results[cursor]) {
      e.preventDefault();
      openHit(results[cursor]);
    } else if (e.key === "Escape") {
      setQ("");
      inputRef.current?.blur();
    }
  };

  return (
    <div className="px-3 pt-3 pb-1 relative" data-testid="sidebar-jumpto">
      <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition ${q ? "border-[#c4a454]/60 bg-[#0b192c]/40" : "border-white/10 bg-black/20 hover:bg-black/25"}`}>
        <Search size={13} className="text-white/40 flex-shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Jump to any module…"
          aria-label="Jump to module"
          className="flex-1 bg-transparent outline-none text-[12.5px] text-white placeholder:text-white/40"
          data-testid="sidebar-jumpto-input"
        />
        {q ? (
          <button type="button" onClick={() => setQ("")}
                  className="text-white/40 hover:text-white flex-shrink-0"
                  aria-label="Clear search"
                  data-testid="sidebar-jumpto-clear">
            <X size={12} />
          </button>
        ) : (
          <span className="text-[10px] text-white/30 font-mono px-1 py-0.5 rounded border border-white/10 flex-shrink-0">/</span>
        )}
      </div>

      {q ? (
        <div className="mt-1 rounded-md border border-white/10 bg-[#0b192c] shadow-lg overflow-hidden"
             data-testid="sidebar-jumpto-results">
          {results.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-white/50">
              No module matches &ldquo;<span className="text-white/80">{q}</span>&rdquo;
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {results.map((hit, i) => (
                <li key={hit.to}>
                  <button
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => openHit(hit)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition ${i === cursor ? "bg-white/8" : "hover:bg-white/5"}`}
                    style={i === cursor ? { background: "rgba(196,164,84,0.12)" } : undefined}
                    data-testid={`sidebar-jumpto-hit-${hit.testId}`}
                  >
                    <hit.icon size={13} strokeWidth={1.6} className="text-white/60 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] text-white leading-tight truncate">
                        <Highlight text={hit.label} term={q} />
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5 uppercase tracking-wider">
                        {hit.section}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Renders `text` with matches of `term` wrapped in a gold-tinted span so
// operators can see exactly why a row survived the filter.
function Highlight({ text = "", term = "" }) {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: "#c4a454" }}>{text.slice(idx, idx + term.length)}</span>
      {text.slice(idx + term.length)}
    </>
  );
}
